/**
 * ResultsDesignTokens - Design tokens, animations, and shared constants
 * for the Flux PhD simulation results dashboard.
 */

import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';

// === Design Tokens ===
export const COLORS = {
    accent: '#9b59b6',
    efficiency: '#00ff88',
    solar: '#ffaa00',
    ev: '#00d4aa',
    grid: '#4488ff',
    loss: '#ff4466',
    export: '#88ff44',
    bess: '#aa88ff',
    dc: '#ff6b6b',
    ac: '#4ecdc4',
    bg: {
        primary: 'rgba(8, 8, 12, 0.95)',
        card: 'rgba(20, 20, 32, 0.85)',
    },
    text: {
        primary: '#e8e4dc',
        secondary: '#888888',
        muted: '#555555',
    }
};

// === Component Forecasting Colors ===
export const COMPONENT_COLORS = {
    hvac: '#ff4466',        // same as COLORS.loss
    refrigeration: '#4488ff', // same as COLORS.grid
    plug_loads: '#ffaa00',   // same as COLORS.solar
    lighting: '#ffee44',
};

// === Animations ===
export const pulseGlow = keyframes`
    0%, 100% { filter: drop-shadow(0 0 20px currentColor); opacity: 1; }
    50% { filter: drop-shadow(0 0 40px currentColor); opacity: 0.9; }
`;

export const fadeInUp = keyframes`
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
`;

export const flowAnimation = keyframes`
    0% { stroke-dashoffset: 24; }
    100% { stroke-dashoffset: 0; }
`;

export const flowAnimationReverse = keyframes`
    0% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: 24; }
`;

export const pulseNode = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
`;

// === Methodology Content ===

export const METHODOLOGY_CONTENT = {
    solar: {
        title: 'Solar Generation Model',
        description: 'Solar output follows a Gaussian bell curve peaking at solar noon. Generation starts at 6 AM and ends at 6 PM, with peak output occurring at 12 PM.',
        formula: 'P(t) = Pmax \u00d7 exp(-(t-12)\u00b2 / 18)',
        note: 'Real-world generation varies with weather, panel orientation, and seasonal sun angle.',
    },
    ev: {
        title: 'EV Charging Load Model',
        description: 'Load profiles vary by use case: Commercial peaks during business hours (9-5), Fleet Depot charges overnight, Retail has evening peaks, and Residential shows morning/evening demand.',
        formula: 'Load = Chargers \u00d7 Power \u00d7 Utilization(t)',
        note: 'Actual demand depends on vehicle arrival patterns and battery state of charge.',
    },
    bess: {
        title: 'Battery Energy Storage',
        description: 'Battery efficiency follows a C-rate dependent model. Higher charge/discharge rates increase internal resistance losses. Round-trip efficiency typically ranges from 85-95%.',
        formula: '\u03b7 = \u03b7_charge \u00d7 \u03b7_discharge \u2248 0.97 \u00d7 0.97 = 94%',
        note: 'Efficiency improves at lower C-rates. Calendar aging and temperature also affect performance.',
    },
    grid: {
        title: 'Grid Import/Export',
        description: 'Grid power fills the gap when solar + battery cannot meet demand. Export occurs when generation exceeds load and battery is full.',
        formula: 'Grid = Load - Solar - BESS_discharge',
        note: 'Grid efficiency accounts for transformer and distribution losses (~2-3%).',
    },
    losses: {
        title: 'System Losses Calculation',
        description: 'Total losses include inverter conversion losses (Sandia CEC model), battery internal resistance, transformer losses, and cable losses. DC-coupled systems eliminate one conversion step.',
        formula: 'Losses = \u03a3(Pin - Pout) for each component',
        note: 'Inverter efficiency varies with load: ~96% at full load, ~89% at 10% load.',
    },
    efficiency: {
        title: 'Round-Trip Efficiency',
        description: 'System efficiency measures useful energy delivered vs. total energy input. It captures all losses in the power conversion chain from source to load.',
        formula: '\u03b7 = (Energy to Load) / (Solar + Grid Import) \u00d7 100%',
        note: 'Higher efficiency means less wasted energy and lower operating costs.',
    },
};

// === Utility Components ===

// CRT Scanline overlay effect
export const ScanlineOverlay = () => (
    <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        background: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0, 0, 0, 0.02) 2px,
            rgba(0, 0, 0, 0.02) 4px
        )`,
        zIndex: 10,
    }} />
);
